import React, { useState, useEffect } from 'react';

export default function AdminManagement() {
  const [adminUsers, setAdminUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newAdmin, setNewAdmin] = useState({ username: '', password: '' });
  const [creating, setCreating] = useState(false);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

  useEffect(() => {
    fetchAdminUsers();
  }, []);

  async function fetchAdminUsers() {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/admin/users`, {
        cache: 'no-cache',
        credentials: 'include',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });
      const data = await response.json();
      
      if (response.ok) {
        setAdminUsers(data);
      } else {
        setError(data.error || 'Failed to fetch admin users');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error fetching admin users:', err);
    } finally {
      setLoading(false);
    }
  }

  async function handleCreateAdmin(e) {
    e.preventDefault();
    
    if (!newAdmin.username || !newAdmin.password) {
      setError('Username and password are required');
      return;
    }

    if (newAdmin.password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    try {
      setCreating(true);
      setError('');
      
      const response = await fetch(`${API_BASE_URL}/api/admin/users`, {
        method: 'POST',
        cache: 'no-cache',
        headers: { 
          'Content-Type': 'application/json',
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        },
        credentials: 'include',
        body: JSON.stringify(newAdmin)
      });

      const data = await response.json();

      if (response.ok) {
        setNewAdmin({ username: '', password: '' });
        setShowCreateForm(false);
        fetchAdminUsers(); // Refresh the list
      } else {
        setError(data.error || 'Failed to create admin user');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error creating admin user:', err);
    } finally {
      setCreating(false);
    }
  }

  async function handleDeleteAdmin(id, username) {
    if (!window.confirm(`Are you sure you want to delete admin user "${username}"?`)) {
      return;
    }

    try {
            const response = await fetch(`${API_BASE_URL}/api/admin/users/${id}`, {
        method: 'DELETE',
        cache: 'no-cache',
        credentials: 'include',
        headers: {
          'Cache-Control': 'no-cache, no-store, must-revalidate',
          'Pragma': 'no-cache',
          'Expires': '0'
        }
      });

      const data = await response.json();

      if (response.ok) {
        fetchAdminUsers(); // Refresh the list
      } else {
        setError(data.error || 'Failed to delete admin user');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Error deleting admin user:', err);
    }
  }

  if (loading) {
    return <div style={{ padding: '1rem' }}>Loading admin users...</div>;
  }

  return (
    <div style={{ marginTop: '2rem' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '1rem'
      }}>
        <div>
          <h3 style={{ margin: 0 }}>Admin User Management</h3>
          <p style={{ margin: '0.25rem 0 0 0', fontSize: '12px', color: '#666' }}>
            Only admin1 (super admin) can manage admin accounts
          </p>
        </div>
        <button
          onClick={() => setShowCreateForm(!showCreateForm)}
          style={{
            padding: '0.5rem 1rem',
            backgroundColor: '#1976d2',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontSize: '14px'
          }}
        >
          {showCreateForm ? 'Cancel' : 'Add Admin'}
        </button>
      </div>

      {error && (
        <div style={{
          backgroundColor: '#ffebee',
          color: '#c62828',
          padding: '0.75rem',
          borderRadius: '4px',
          marginBottom: '1rem',
          fontSize: '14px',
          border: '1px solid #ffcdd2'
        }}>
          {error}
        </div>
      )}

      {showCreateForm && (
        <div style={{
          backgroundColor: '#f5f5f5',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '1rem'
        }}>
          <h4 style={{ margin: '0 0 1rem 0' }}>Create New Admin User</h4>
          <form onSubmit={handleCreateAdmin}>
            <div style={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr auto',
              gap: '1rem',
              alignItems: 'end'
            }}>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}>
                  Username
                </label>
                <input
                  type="text"
                  value={newAdmin.username}
                  onChange={(e) => setNewAdmin({ ...newAdmin, username: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    boxSizing: 'border-box'
                  }}
                  placeholder="Enter username"
                  disabled={creating}
                />
              </div>
              <div>
                <label style={{
                  display: 'block',
                  marginBottom: '0.5rem',
                  fontWeight: 'bold',
                  fontSize: '14px'
                }}>
                  Password
                </label>
                <input
                  type="password"
                  value={newAdmin.password}
                  onChange={(e) => setNewAdmin({ ...newAdmin, password: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    boxSizing: 'border-box'
                  }}
                  placeholder="Enter password (min 6 chars)"
                  disabled={creating}
                />
              </div>
              <button
                type="submit"
                disabled={creating}
                style={{
                  padding: '0.5rem 1rem',
                  backgroundColor: creating ? '#ccc' : '#4caf50',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: creating ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  whiteSpace: 'nowrap'
                }}
              >
                {creating ? 'Creating...' : 'Create'}
              </button>
            </div>
          </form>
        </div>
      )}

      <table border="1" cellPadding="8" style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ backgroundColor: '#f5f5f5' }}>
            <th>ID</th>
            <th>Username</th>
            <th>Created By</th>
            <th>Created At</th>
            <th>Last Login</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {adminUsers.map(user => (
            <tr key={user.id}>
              <td>{user.id}</td>
              <td style={{ fontWeight: 'bold' }}>{user.username}</td>
              <td>{user.created_by || '—'}</td>
              <td>{user.createdAt ? new Date(user.createdAt).toLocaleString() : '—'}</td>
              <td>{user.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'Never'}</td>
              <td>
                {user.username === 'admin1' ? (
                  <span style={{
                    padding: '0.25rem 0.5rem',
                    backgroundColor: '#f5f5f5',
                    color: '#666',
                    border: '1px solid #ddd',
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}>
                    Super Admin
                  </span>
                ) : (
                  <button
                    onClick={() => handleDeleteAdmin(user.id, user.username)}
                    style={{
                      padding: '0.25rem 0.5rem',
                      backgroundColor: '#d32f2f',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '12px'
                    }}
                  >
                    Delete
                  </button>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {adminUsers.length === 0 && (
        <div style={{
          textAlign: 'center',
          padding: '2rem',
          color: '#666',
          fontStyle: 'italic'
        }}>
          No admin users found
        </div>
      )}
    </div>
  );
}
